import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { useSearchParams } from 'react-router-dom';

import Button from '../../common/ui/Button';
import ArrowIcon from '../../../assets/icons/common/ArrowIcon';
import DeviceDetailsModal from '../DeviceDetailsModal';
import CopyToClipboard from '../../common/features/CopyToClipboard';
import DeviceUsage from '../DeviceUsage';
import useWindowSize from '../../../hooks/useWindowSize';
import DeviceStatus from '../DeviceStatus';
import { getDeviceSn, isInPossibleReactivation } from '../../../utils/helpers';
import { openGladlyChat } from '../../../utils/gladly';
import DeviceVariants from '../DeviceVariants';
import DeviceSubCancellation from '../DeviceSubCancellation';
import { getFormattedNextChargeDate } from '../../../utils/dates';
import DeviceReturn from '../DeviceReturn';
import {
  useLazyGetReturnLabelQuery,
  useGetReturnStatusQuery,
} from '../../../redux/returnLabelsApi';
import DeviceReactivation from '../DeviceReactivation';
import fetchRequest from '../../../utils/fetchRequest';
import useNoty from '../../../hooks/useNoty';
import { useReactivation } from '../../../hooks/useReactivation';

import styles from './styles.module.scss';

const Device = ({ subscription, refetch }) => {
  const expired = !!subscription?.cancelled_at;
  const [searchParams, setSearchParams] = useSearchParams();
  const [reactivationLoading, setReactivationLoading] = useState(false);
  const [showReactivationButton, setShowReactivationButton] = useState(expired);
  const windowSize = useWindowSize();
  const isResponsive = windowSize?.width < 950;
  const selectedVariantId = subscription?.external_variant_id?.ecommerce;

  const [deviceDetailsModalIsOpen, setDeviceDetailsModalIsOpen] =
    useState(false);
  const noty = useNoty();
  const { handleReactivation } = useReactivation({ noty, refetch });
  const device_info = subscription?.meta?.device_info || {};
  const meta = subscription?.meta || {};

  const subId = subscription?.id;
  const inPossibleReactivation = isInPossibleReactivation(subscription);
  const nextChargeDate = getFormattedNextChargeDate(
    subscription?.next_charge_scheduled_at,
  );
  const { title, largeImage } = meta;
  const defaultImagePath = 'images/products/default-product-image.png';
  const connectionStatusName =
    device_info?.connection_status_name?.toLowerCase();
  const signalLevel = device_info?.last_known_vsim_signal_strength || 0;
  const deviceSn = getDeviceSn(subscription?.properties);
  const addressId = subscription?.address_id;
  const [fetchReturnLabel, { data: returnData }] = useLazyGetReturnLabelQuery();
  const { data: returnStatus } = useGetReturnStatusQuery(subId, {
    skip: !expired,
  });

  const returnLabel = returnData?.returnLabel;
  const toggleDetailModal = () => {
    setDeviceDetailsModalIsOpen(!deviceDetailsModalIsOpen);
  };

  const handleSuccessUpdatePlan = () => {
    if (!searchParams.get('subscription_id')) {
      setSearchParams({ subscription_id: subId });
    }
    refetch();
  };

  const handleCancelSubscription = async () => {
    if (!searchParams.get('subscription_id')) {
      setSearchParams({ subscription_id: subId });
    }
    await refetch();
  };

  useEffect(() => {
    if (expired) {
      fetchReturnLabel(subId);
    }
  }, [expired, subId, fetchReturnLabel]);
  const reactivationSubmitHandle = async () => {
    setReactivationLoading(true);
    try {
      await fetchRequest.post(`/subscriptions/${subId}/reactivation`);
      noty.success({
        value:
          'We`re activating your subscription now. This may take up to 5 minutes.',
        disableAutoClose: true,
      });
      setShowReactivationButton(false);
      const result = await handleReactivation(subId);
      if (result.status === 'failed' && result.errorType === 'payment') {
        setShowReactivationButton(true);
      }
    } catch (e) {
      setReactivationLoading(false);
      noty.error({
        value: e.message || 'Something went wrong. Please, contact our support',
        disableAutoClose: true,
      });
    } finally {
      setReactivationLoading(false);
    }
  };

  useEffect(() => {
    if (inPossibleReactivation === true) {
      handleReactivation(subId);
    }
    // eslint-disable-next-line
  }, [inPossibleReactivation]);

  useEffect(() => {
    setShowReactivationButton(!!subscription?.cancelled_at);
  }, [subscription, setShowReactivationButton]);

  return (
    <div className={styles.device}>
      <h1 className={cn('h2', styles.deviceTitle)}>{title}</h1>
      <div className={styles.deviceWrapper}>
        <img
          src={largeImage || defaultImagePath}
          className={styles.deviceImg}
          alt={title}
        />
        <div className={styles.deviceContent}>
          {!isResponsive && (
            <DeviceUsage subscription={subscription}></DeviceUsage>
          )}
          <div className={styles.deviceInfo}>
            <DeviceStatus
              connectionStatusName={connectionStatusName}
              signalLevel={signalLevel}
              deviceSn={deviceSn}
              className={styles.deviceInfoItem}
              isExpired={expired}
              showPieChart={true}
              refetch={refetch}
            ></DeviceStatus>
            <div
              className={cn(
                styles.deviceInfoSerialNumber,
                styles.deviceInfoItem,
              )}
            >
              <span>Serial Number</span>
              <p>
                {deviceSn}
                <CopyToClipboard text={deviceSn}></CopyToClipboard>
              </p>
            </div>
          </div>
          {!isResponsive && (
            <div className={styles.deviceButtons}>
              {expired && (
                <DeviceReturn
                  returnLabel={returnLabel}
                  deliveryStatus={returnStatus?.trackingStatus}
                  inspectionStatus={returnStatus?.inspectionStatus}
                ></DeviceReturn>
              )}
              {!expired && (
                <button
                  className={styles.deviceButtonsModal}
                  onClick={toggleDetailModal}
                >
                  All Data <ArrowIcon></ArrowIcon>
                </button>
              )}
              {connectionStatusName !== 'online' && (
                <Button onClick={openGladlyChat} colorType={'grey'}>
                  Need help?
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      {isResponsive && <DeviceUsage subscription={subscription}></DeviceUsage>}
      {isResponsive && (
        <div className={styles.deviceButtons}>
          {!expired && (
            <button
              className={styles.deviceButtonsModal}
              onClick={toggleDetailModal}
            >
              All Data <ArrowIcon></ArrowIcon>
            </button>
          )}
          {connectionStatusName !== 'online' && (
            <Button colorType={'grey'} onClick={openGladlyChat}>
              Need help?
            </Button>
          )}
          {expired && (
            <DeviceReturn
              returnLabel={returnLabel}
              deliveryStatus={returnStatus?.trackingStatus}
              inspectionStatus={returnStatus?.inspectionStatus}
            ></DeviceReturn>
          )}
        </div>
      )}
      <DeviceVariants
        handleSuccessUpdate={handleSuccessUpdatePlan}
        subscription={subscription}
        selectedVariantId={selectedVariantId}
        isSubCancelled={expired}
      ></DeviceVariants>
      <DeviceSubCancellation
        subId={subId}
        addressId={addressId}
        deviceSn={deviceSn}
        className={styles.deviceSubDetails}
        expired={expired}
        nextChargeDate={nextChargeDate}
        handleCancelSub={handleCancelSubscription}
        returnLabel={returnLabel}
      ></DeviceSubCancellation>
      {showReactivationButton && (
        <DeviceReactivation
          subId={subId}
          className={styles.deviceSubDetails}
          cancelledAt={subscription?.cancelled_at}
          reactivationHandle={reactivationSubmitHandle}
          isLoading={reactivationLoading}
        />
      )}
      <DeviceDetailsModal
        subscription={subscription}
        toggleDetailModal={toggleDetailModal}
        isOpen={deviceDetailsModalIsOpen}
      ></DeviceDetailsModal>
    </div>
  );
};

export default Device;
